/**
 * CWB Master Layout — shared widget-type & conditional configuration.
 *
 * Single source of truth for:
 *   - the canonical list of widget types a canonical question can render as
 *   - the operators / logic used by the structured conditional builder
 *
 * Consumed by the canonical-question editor, the Master Layout placement
 * editor (widget override), the structured conditional builder, and the
 * questionLibrary Vuex module. Keep every widget-type value in THIS file so
 * the authoring UI and the (future) generic CWB renderer stay in agreement.
 */

/**
 * Widget types.
 *  - value:                persisted `widget_type` value (backend contract)
 *  - label:                human-readable primary UI text
 *  - supportsAnswerOptions whether the widget renders authored answer_options
 */
export const WIDGET_TYPES = [
  { value: "yes_no", label: "Yes / No", supportsAnswerOptions: false },
  { value: "text", label: "Text", supportsAnswerOptions: false },
  { value: "number", label: "Number", supportsAnswerOptions: false },
  { value: "date", label: "Date", supportsAnswerOptions: false },
  { value: "percentage", label: "Percentage", supportsAnswerOptions: false },
  { value: "currency", label: "Currency ($)", supportsAnswerOptions: false },
  { value: "phone", label: "Phone Number", supportsAnswerOptions: false },
  { value: "multi_select", label: "Multi-select", supportsAnswerOptions: true },
  { value: "single_select", label: "Single-select", supportsAnswerOptions: true },
  { value: "platform_tiles", label: "Platform tiles", supportsAnswerOptions: true },
  { value: "vendor_repeater", label: "Vendor repeater", supportsAnswerOptions: false },
  { value: "provider_repeater", label: "Provider repeater", supportsAnswerOptions: false },
  { value: "address", label: "Address", supportsAnswerOptions: false },
  { value: "logo", label: "Logo", supportsAnswerOptions: false },
  { value: "industry_select", label: "Industry select", supportsAnswerOptions: true },
  { value: "team", label: "Team", supportsAnswerOptions: false },
  { value: "market_select", label: "Market select", supportsAnswerOptions: true },
  { value: "file_upload", label: "File upload", supportsAnswerOptions: false },
];

/** Set of valid widget-type values for O(1) validation. */
export const WIDGET_TYPE_VALUES = WIDGET_TYPES.map((w) => w.value);

/**
 * v-select items for the canonical-question editor's "Widget Type" field.
 * `text`/`value` shape matches the existing Vuetify selects. An empty value
 * means "derived from answer type", so consumers set that as `placeholder`
 * and may be `clearable`. Never add a `null` item: Vuetify would show it as
 * selected, with a clear icon that cannot clear it.
 */
export const WIDGET_TYPE_SELECT_ITEMS = WIDGET_TYPES.map((w) => ({ text: w.label, value: w.value }));

/**
 * v-select items for a placement's Widget Override. Same list, but the
 * empty option reads "Use canonical widget" because a placement inherits
 * the canonical question's widget unless overridden. Its `null` item is the
 * only way to pick "no override", so never pair this list with `clearable`.
 */
export const WIDGET_OVERRIDE_SELECT_ITEMS = [
  { text: "Use canonical widget (no override)", value: null },
  ...WIDGET_TYPES.map((w) => ({ text: w.label, value: w.value })),
];

/** Map widget-type value -> human label (falls back to the raw value). */
export function widgetTypeLabel(value) {
  if (value === null || value === undefined || value === "") return "Derived from answer type";
  const match = WIDGET_TYPES.find((w) => w.value === value);
  return match ? match.label : value;
}

export function isValidWidgetType(value) {
  return WIDGET_TYPE_VALUES.includes(value);
}

export function widgetSupportsAnswerOptions(value) {
  const match = WIDGET_TYPES.find((w) => w.value === value);
  return Boolean(match?.supportsAnswerOptions);
}

/* ------------------------------------------------------------------ */
/* Structured conditional builder                                      */
/* ------------------------------------------------------------------ */

/**
 * Comparison operators supported by a conditional clause.
 *  - value:        persisted operator token (backend conditional contract)
 *  - label:        primary UI text
 *  - valueArity:   how many operand values the clause carries
 *                    "none"   -> operator needs no value (unused today)
 *                    "single" -> a single `value`
 *                    "list"   -> a `values` array (includes / in-list style)
 *                    "range"  -> `min` and `max` (in-range)
 */
export const CONDITIONAL_OPERATORS = [
  { value: "==", label: "equals (==)", valueArity: "single" },
  { value: "!=", label: "not equals (!=)", valueArity: "single" },
  { value: ">", label: "greater than (>)", valueArity: "single" },
  { value: ">=", label: "greater or equal (>=)", valueArity: "single" },
  { value: "<", label: "less than (<)", valueArity: "single" },
  { value: "<=", label: "less or equal (<=)", valueArity: "single" },
  { value: "includes", label: "includes", valueArity: "list" },
  { value: "in-range", label: "in range", valueArity: "range" },
];

export const CONDITIONAL_OPERATOR_VALUES = CONDITIONAL_OPERATORS.map((o) => o.value);

/** Logical joiners between clauses. */
export const LOGIC_OPERATORS = ["AND", "OR"];

export function operatorValueArity(operator) {
  const match = CONDITIONAL_OPERATORS.find((o) => o.value === operator);
  return match ? match.valueArity : "single";
}

export function operatorLabel(operator) {
  const match = CONDITIONAL_OPERATORS.find((o) => o.value === operator);
  return match ? match.label : operator;
}
